import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { MapPin, Mail, Linkedin, Phone, Building2, CheckCircle, Leaf, Factory, Droplets, ChevronRight, Award, Users, Landmark, Menu, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import TIL_LOGO_IMG from "./til-logo.png";
import RAHUL_PHOTO from "./rahul-photo.png";
import PRATIMA_PHOTO from "./pratima-photo.png";
import TANAY_PHOTO from "./tanay-photo.png";
const TIL_LOGO = TIL_LOGO_IMG;

// Images
const HERO_IMAGE = "https://images.unsplash.com/photo-1768128834301-7811be9d3a1f?crop=entropy&cs=srgb&fm=jpg&w=1920";
const INDUSTRIAL_IMAGE = "https://images.pexels.com/photos/5411674/pexels-photo-5411674.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ABOUT_INDUSTRIAL_IMAGE = "https://images.pexels.com/photos/3089681/pexels-photo-3089681.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WAREHOUSE_IMAGE = "https://images.unsplash.com/photo-1760921678729-9658c8b792bb?crop=entropy&cs=srgb&fm=jpg&w=800";
const WASTEWATER_IMAGE = "https://images.pexels.com/photos/3808769/pexels-photo-3808769.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const OPERATIONS_IMAGE = "https://images.pexels.com/photos/36423820/pexels-photo-36423820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PAPER_PULP_IMAGE = "https://images.unsplash.com/photo-1756742878998-9d8cd5360a92?crop=entropy&cs=srgb&fm=jpg&w=800";
const INDUSTRIAL_CHEM_IMAGE = "https://images.unsplash.com/photo-1756742706198-36b949f79f32?crop=entropy&cs=srgb&fm=jpg&w=800";

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("");

  // Scroll progress bar + active nav section
  useEffect(() => {
    const handleScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);

      const sections = ["about", "industries", "products", "facilities", "investors", "contact"];
      let current = "";
      sections.forEach((id) => {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 100) current = id;
      });
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Parallax on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      document.querySelectorAll(".parallax-bg").forEach((el) => {
        const speed = parseFloat(el.dataset.speed) || 0.4;
        el.style.transform = `translateY(${scrollY * speed}px)`;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fade-in on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.15 }
    );
    document.querySelectorAll(".fade-in").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formDataEncoded = new URLSearchParams();
      formDataEncoded.append('form-name', 'contact');
      Object.keys(formData).forEach(key => formDataEncoded.append(key, formData[key]));
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataEncoded.toString()
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(null), 3000);
  };



  const sectors = [
    { title: "Agriculture & Fertilizers", desc: "Premium compounds for crop nutrition and agricultural growth", img: "https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=800" },
    { title: "Mining & Explosives", desc: "Industrial grade solutions for mining operations", img: "https://images.pexels.com/photos/4946886/pexels-photo-4946886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
    { title: "Wastewater Treatment", desc: "Municipal & industrial effluent treatment solutions", img: WASTEWATER_IMAGE },
    { title: "Paper & Pulp", desc: "Specialty chemicals for paper manufacturing", img: PAPER_PULP_IMAGE },
    { title: "Industrial Chemicals", desc: "Wide range of industrial chemical solutions", img: INDUSTRIAL_CHEM_IMAGE },
    { title: "Infrastructure & Construction", desc: "Building materials and construction chemicals", img: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800" },
    { title: "Emulsifiers & Lubricants", desc: "High-performance emulsifiers and industrial lubricants", img: "https://images.pexels.com/photos/4980786/pexels-photo-4980786.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  ];

  const products = [
    { category: "Chemicals", name: "Nitrate Derivatives" },
    { category: "Chemicals", name: "Emulsifiers" },
    { category: "Chemicals", name: "Lubricants" },
    { category: "Chemicals", name: "Solvents" },
    { category: "Chemicals", name: "Solutions" },
  ];

  const manufacturingUnits = [
    "Maharashtra",
    "Madhya Pradesh",
    "Telangana",
    "Jharkhand",
    "Chhattisgarh"
  ];

  return (
    <div className="min-h-screen bg-slate-900" data-testid="tanay-homepage">
      {/* Scroll progress bar */}
      <div className="fixed top-0 left-0 z-[9999] h-[3px] bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-100" style={{ width: `${scrollProgress}%` }} />

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-4">
              <div style={{ borderRadius: '50%', overflow: 'hidden', width: '64px', height: '64px', flexShrink: 0 }}>
                <img src={TIL_LOGO} alt="TIL Logo" className="h-full w-full object-cover" data-testid="til-logo" />
              </div>
              <span className="text-white text-xl font-bold tracking-wide hidden sm:block">TANAY INORGANICS LTD.</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              {[{id:"about",label:"About"},{id:"industries",label:"Industries"},{id:"products",label:"Products"},{id:"facilities",label:"Facilities"},{id:"investors",label:"Investors"}].map((s) => (
                <a key={s.id} href={`#${s.id}`} className={`transition-colors ${activeSection === s.id ? "text-purple-400 font-semibold" : "text-slate-300 hover:text-white"}`}>{s.label}</a>
              ))}
              <a href="#contact" className={`px-4 py-2 rounded-lg transition-opacity ${activeSection === "contact" ? "bg-purple-500 text-white" : "bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:opacity-90"}`}>Contact Us</a>
            </div>
            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              <a href="#about" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white transition-colors py-2">About</a>
              <a href="#industries" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white transition-colors py-2">Industries</a>
              <a href="#products" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white transition-colors py-2">Products</a>
              <a href="#facilities" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white transition-colors py-2">Facilities</a>
              <a href="#investors" onClick={() => setMobileMenuOpen(false)} className="block text-slate-300 hover:text-white transition-colors py-2">Investors</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity text-center">Contact Us</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <img src={HERO_IMAGE} alt="Industrial Chemical Plant" className="parallax-bg w-full h-[120%] object-cover -top-[10%] absolute" data-speed="0.3" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-slate-900/70"></div>
          {/* Floating particles */}
          {[...Array(18)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              animationDuration: `${4 + Math.random() * 6}s`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: 0.3 + Math.random() * 0.4,
            }} />
          ))}
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-semibold px-4 py-2 rounded-full" data-testid="established-badge">
                  ESTABLISHED 2022
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                Industrial Chemical
              </h1>
              <h1 className="text-4xl md:text-6xl font-bold hero-gradient-text mb-6">
                Excellence
              </h1>
              <p className="text-lg text-slate-300 mb-4">
                State of the Art Manufacturing Unit
              </p>
              <p className="text-slate-400 mb-6">
                Delivering quality, innovation, and sustainable chemical solutions across diverse industrial sectors.
              </p>
              <p className="text-purple-300 font-medium mb-8 border-l-4 border-purple-500 pl-4" data-testid="pioneer-text">
                We are <span className="text-white font-bold">50+ years</span> experienced pioneers and market leaders in the chemical sector with our parent companies' dedication and determination.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#industries" className="btn-shine bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center gap-2" data-testid="explore-industries-btn">
                  Explore Industries <ChevronRight size={20} />
                </a>
                <a href="#contact" className="border border-slate-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors" data-testid="get-quote-btn">
                  Get Quote
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <img 
                src={INDUSTRIAL_IMAGE} 
                alt="Industrial facility" 
                className="rounded-2xl shadow-2xl border border-slate-700"
                data-testid="hero-industry-image"
              />
            </div>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mt-16 pt-8 border-t border-slate-800">
            <div className="text-center fade-in fade-in-delay-1" data-testid="stats-experience">
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent stat-number">50+</div>
              <div className="text-slate-400">Years Experience</div>
            </div>
            <div className="text-center fade-in fade-in-delay-2" data-testid="stats-clients">
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent stat-number">413+</div>
              <div className="text-slate-400">Clients Served</div>
            </div>
            <div className="text-center fade-in fade-in-delay-3" data-testid="stats-units">
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent stat-number">5+</div>
              <div className="text-slate-400">Manufacturing Units</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-800" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <span className="text-purple-400 font-semibold">About Us</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12 fade-in">
            Leading the Way in Industrial Chemicals
          </h2>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-slate-300 text-lg mb-6">
                Tanay Inorganics Ltd. is one of India's leading manufacturers of industrial chemicals and solutions since 2022. We are committed to excellence in chemical manufacturing, delivering high-quality products that meet the diverse needs of various industrial sectors.
              </p>
              <p className="text-slate-300 mb-6">
                <span className="text-white font-semibold">Catering to Pan-India & International Markets:</span> We proudly serve customers across India and abroad, establishing ourselves as a trusted partner for industrial chemical solutions. Our extensive distribution network ensures timely delivery to clients nationwide and in international markets.
              </p>
              <p className="text-slate-300 mb-6">
                <span className="text-white font-semibold">High Quality Supply & Consistency:</span> Our commitment to quality is unwavering. We maintain stringent quality control measures at every stage of production, ensuring that our customers receive products that meet the highest industry standards. Our state-of-the-art manufacturing facilities are equipped with modern machinery and follow ISO certified processes.
              </p>
              <p className="text-purple-300 font-medium mb-8 bg-slate-900/50 p-4 rounded-lg border border-purple-500/30" data-testid="mother-company-text">
                We are <span className="text-white font-bold">50+ years</span> experienced pioneers and market leaders in the chemical sector with our parent companies' dedication and determination.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-purple-400 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold">Quality Assured</h4>
                    <p className="text-slate-400 text-sm">ISO certified processes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Leaf className="text-green-400 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold">Eco Friendly</h4>
                    <p className="text-slate-400 text-sm">Sustainable practices</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="text-yellow-400 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold">Pan-India Reach</h4>
                    <p className="text-slate-400 text-sm">Nationwide delivery</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="text-blue-400 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold">Global Exports</h4>
                    <p className="text-slate-400 text-sm">International markets</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img src={ABOUT_INDUSTRIAL_IMAGE} alt="Industrial Facility" className="rounded-2xl shadow-xl" />
              <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-4 rounded-xl">
                <div className="text-sm">Since</div>
                <div className="text-3xl font-bold" data-testid="about-since-year">2022</div>
              </div>
            </div>
          </div>

          {/* Manufacturing Units */}
          <div className="mt-16 pt-12 border-t border-slate-700">
            <h3 className="text-2xl font-bold text-white text-center mb-8" data-testid="manufacturing-units-title">
              5+ Manufacturing Units
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {manufacturingUnits.map((state, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-slate-900 px-6 py-3 rounded-full" data-testid={`unit-${state.toLowerCase().replace(' ', '-')}`}>
                  <MapPin className="text-purple-400" size={18} />
                  <span className="text-white font-medium">{state}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Board Members */}
          <div className="mt-16 pt-12 border-t border-slate-700">
            <div className="text-center mb-10">
              <span className="text-purple-400 font-semibold">Leadership</span>
              <h3 className="text-2xl md:text-3xl font-bold text-white mt-2">Our Board Members</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Rahul Goel */}
              <div className="bg-slate-900 rounded-2xl p-8 text-center card-hover fade-in fade-in-delay-1">
                <div className="w-28 h-28 mx-auto mb-5 rounded-full overflow-hidden border-2 border-purple-500">
                  <img src={RAHUL_PHOTO} alt="Rahul Goel" className="w-full h-full object-cover" />
                </div>
                <h4 className="text-white text-xl font-bold mb-1">Rahul Goel</h4>
                <p className="text-purple-400 font-semibold text-sm mb-4">Chairman & Managing Director</p>
                <div className="w-12 h-0.5 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto mb-4"></div>
                <ul className="text-slate-400 text-sm text-left space-y-2">
                  <li>• 28+ years of experience in chemical manufacturing & multi-site operations</li>
                  <li>• Founder/Director of Rasayan Udyog — 15+ years driving consistent business growth</li>
                  <li>• MBA Finance; led plant commissioning, supply chain & international sourcing</li>
                  <li>• Director across 7 companies including HVG Logistics & RPG Rasayan Udyog</li>
                  <li>• Spearheading TIL Group's Pan-India expansion across 5+ manufacturing states</li>
                </ul>
              </div>
              {/* Tanay Goel */}
              <div className="bg-slate-900 rounded-2xl p-8 text-center card-hover fade-in fade-in-delay-2">
                <div className="w-28 h-28 mx-auto mb-5 rounded-full overflow-hidden border-2 border-purple-500">
                  <img src={TANAY_PHOTO} alt="Tanay Goel" className="w-full h-full object-cover" />
                </div>
                <h4 className="text-white text-xl font-bold mb-1">Tanay Goel</h4>
                <p className="text-purple-400 font-semibold text-sm mb-4">Director</p>
                <div className="w-12 h-0.5 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto mb-4"></div>
                <ul className="text-slate-400 text-sm text-left space-y-2">
                  <li>• A young, fiercely ambitious leader determined to take TIL Group to a global stage</li>
                  <li>• Brings fresh vision and relentless drive to scale the company across world markets</li>
                  <li>• Deeply passionate about innovation, operational excellence, and sustainable growth</li>
                  <li>• Combines the family's 50+ year legacy with a bold, future-forward mindset</li>
                  <li>• Focused on expanding TIL's international footprint and building a world-class brand</li>
                </ul>
              </div>
              {/* Pratima Goel */}
              <div className="bg-slate-900 rounded-2xl p-8 text-center card-hover fade-in fade-in-delay-3">
                <div className="w-28 h-28 mx-auto mb-5 rounded-full overflow-hidden border-2 border-purple-500">
                  <img src={PRATIMA_PHOTO} alt="Pratima Goel" className="w-full h-full object-cover" />
                </div>
                <h4 className="text-white text-xl font-bold mb-1">Pratima Goel</h4>
                <p className="text-purple-400 font-semibold text-sm mb-4">Director</p>
                <div className="w-12 h-0.5 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto mb-4"></div>
                <ul className="text-slate-400 text-sm text-left space-y-2">
                  <li>• 28+ years of experience in business operations, marketing & brand strategy</li>
                  <li>• MBA Marketing; spearheads client relationship management and market development</li>
                  <li>• Business Operations & Marketing Head across TIL's Umred and Ramakundam facilities</li>
                  <li>• Instrumental in new market entry, vendor coordination, and regulatory liaison</li>
                  <li>• Director across 6 companies including Ginni Iron & HVG Industries</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section id="industries" className="py-20 bg-slate-900" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <span className="text-purple-400 font-semibold">Our Expertise</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Sectors We Serve
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sectors.map((sector, idx) => (
              <div key={idx} className="group relative overflow-hidden rounded-xl bg-slate-800 card-hover fade-in" style={{ transitionDelay: `${idx * 0.08}s` }} data-testid={`sector-card-${idx}`}>
                <div className="aspect-video overflow-hidden">
                  <img src={sector.img} alt={sector.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{sector.title}</h3>
                  <p className="text-slate-400 text-sm">{sector.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-slate-800" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <span className="text-purple-400 font-semibold">Our Products</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Chemical Solutions
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {products.map((product, idx) => (
              <div key={idx} className="bg-slate-900 p-6 rounded-xl hover:bg-slate-700 transition-colors text-center card-hover fade-in" style={{ transitionDelay: `${idx * 0.1}s` }} data-testid={`product-card-${idx}`}>
                <span className="text-purple-400 text-sm font-medium">{product.category}</span>
                <h3 className="text-white font-semibold mt-2 text-lg">{product.name}</h3>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="#contact" className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity inline-block" data-testid="request-catalog-btn">
              Request Product Catalog
            </a>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section id="facilities" className="py-20 bg-slate-900" data-testid="facilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <span className="text-purple-400 font-semibold">Our Facilities</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-6">
            State-of-the-Art Manufacturing
          </h2>
          <p className="text-slate-400 text-center max-w-3xl mx-auto mb-12">
            Our manufacturing facilities are equipped with modern machinery and follow strict quality control measures. We maintain the highest standards of safety and environmental compliance.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {manufacturingUnits.map((state, idx) => (
              <div key={idx} className="bg-slate-800 p-6 rounded-xl flex items-center gap-4" data-testid={`facility-${state.toLowerCase().replace(' ', '-')}`}>
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="text-white font-semibold">{state}</h4>
                  <p className="text-slate-400 text-sm">Manufacturing Unit</p>
                </div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="relative overflow-hidden rounded-xl">
              <img src={WAREHOUSE_IMAGE} alt="Efficient Storage" className="w-full h-64 object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 p-6">
                <span className="text-purple-400 text-sm">Efficient</span>
                <h4 className="text-white text-xl font-bold">Storage</h4>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-xl">
              <img src={OPERATIONS_IMAGE} alt="Safe Operations" className="w-full h-64 object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 p-6">
                <span className="text-purple-400 text-sm">Efficient</span>
                <h4 className="text-white text-xl font-bold">Operations</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative" data-testid="cta-section">
        <div className="absolute inset-0 overflow-hidden">
          <img src={PAPER_PULP_IMAGE} alt="Industrial Background" className="parallax-bg w-full h-[120%] object-cover -top-[10%] absolute" data-speed="0.2" />
          <div className="absolute inset-0 bg-slate-900/90"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-slate-300 mb-8">
            Get in touch for bulk orders, custom requirements, or technical consultation. Our team is ready to assist you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#contact" className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity" data-testid="submit-inquiry-btn">
              Submit Inquiry
            </a>
            <a href="mailto:tanayinorganicsltd@gmail.com" className="border border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors" data-testid="email-us-btn">
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Investor Relations Section */}
      <section id="investors" className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <span className="text-purple-400 font-semibold">Investor Relations</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4 fade-in">
            Invest with TIL Group
          </h2>
          <p className="text-slate-400 text-center max-w-3xl mx-auto mb-12 fade-in">
            Tanay Inorganics Limited is committed to transparency and long-term value creation for all our stakeholders. We invite investors to explore our growth journey.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-slate-800 rounded-2xl p-8 text-center card-hover fade-in fade-in-delay-1">
              <Landmark className="text-purple-400 mx-auto mb-4" size={36} />
              <h4 className="text-white text-xl font-bold mb-2">Company Overview</h4>
              <p className="text-slate-400 text-sm">Founded in 2022 with 50+ years of parent company experience, operating across 5+ states in India with 413+ clients served.</p>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8 text-center card-hover fade-in fade-in-delay-2">
              <Award className="text-purple-400 mx-auto mb-4" size={36} />
              <h4 className="text-white text-xl font-bold mb-2">Growth & Vision</h4>
              <p className="text-slate-400 text-sm">Rapidly expanding across Pan-India and international markets with a diversified product portfolio spanning 7+ industrial sectors.</p>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8 text-center card-hover fade-in fade-in-delay-3">
              <Mail className="text-purple-400 mx-auto mb-4" size={36} />
              <h4 className="text-white text-xl font-bold mb-2">Investor Enquiries</h4>
              <p className="text-slate-400 text-sm mb-4">For investor relations, financial information, or partnership opportunities, reach out to our team directly.</p>
              <a href="mailto:Info@tilgroup.co.in" className="text-purple-400 hover:text-purple-300 font-semibold text-sm">Info@tilgroup.co.in</a>
            </div>
          </div>
          <div className="text-center">
            <a href="#contact" className="btn-shine bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity inline-block">
              Get in Touch with Us
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-800" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <span className="text-purple-400 font-semibold">Get In Touch</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Contact Us
          </h2>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <p className="text-slate-300 mb-8">
                Have questions about our products or services? Fill out the form and our team will get back to you within 24 hours.
              </p>
              <div className="space-y-6">
                <div>
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="text-purple-400" size={20} />
                    Our Locations
                  </h4>
                  <div className="text-slate-400 space-y-2">
                    {manufacturingUnits.map((state, idx) => (
                      <p key={idx}>{state}, India</p>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Mail className="text-purple-400" size={20} />
                    Email
                  </h4>
                  <a href="mailto:tanayinorganicsltd@gmail.com" className="text-purple-400 hover:text-purple-300 block">
                    tanayinorganicsltd@gmail.com
                  </a>
                  <a href="mailto:Info@tilgroup.co.in" className="text-purple-400 hover:text-purple-300 block mt-1">
                    Info@tilgroup.co.in
                  </a>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Linkedin className="text-purple-400" size={20} />
                    Follow Us
                  </h4>
                  <a href="#" className="text-purple-400 hover:text-purple-300">LinkedIn</a>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form" name="contact" data-netlify="true">
              <input
                type="text"
                placeholder="Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                data-testid="contact-name-input"
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                data-testid="contact-email-input"
                required
              />
              <input
                type="tel"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                data-testid="contact-phone-input"
              />
              <input
                type="text"
                placeholder="Company"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                data-testid="contact-company-input"
              />
              <textarea
                placeholder="Message"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                data-testid="contact-message-input"
                required
              ></textarea>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus === 'success' && (
                <p className="text-green-400 text-center">Message sent successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-center">Failed to send message. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer - No Made with Emergent watermark */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center items-center gap-3 mb-4">
            <div style={{ borderRadius: '50%', overflow: 'hidden', width: '56px', height: '56px', flexShrink: 0 }}>
              <img src={TIL_LOGO} alt="TIL Logo" className="h-full w-full object-cover" />
            </div>
            <span className="text-white font-bold tracking-wide">TANAY INORGANICS LTD.</span>
          </div>
          <p className="text-slate-500 text-sm mb-1">
            © 2022-{new Date().getFullYear()} Tanay Inorganics Ltd. All rights reserved.
          </p>
          <p className="text-slate-600 text-xs">
            CIN: [YOUR_CIN_NUMBER]
          </p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
